import type {
  BillingPayment,
  BillingSnapshot,
  BillingViewModel,
  PaymentStatus,
} from '@/components/billing/billing-types';
import type { Locales, TranslationFunctions } from '@/i18n/i18n-types';
import { getLuxonLocale } from '@/lib/utils';
import { DateTime } from 'luxon';

function formatCurrency(
  amount: number,
  currency: string,
  locale: Locales
): string {
  return new Intl.NumberFormat(getLuxonLocale(locale), {
    style: 'currency',
    currency,
  }).format(amount);
}

function formatDate(iso: string, locale: Locales): string {
  return DateTime.fromISO(iso)
    .setLocale(getLuxonLocale(locale))
    .toLocaleString(DateTime.DATE_SHORT);
}

function paymentStatusLabel(
  status: PaymentStatus,
  LL: TranslationFunctions
): string {
  switch (status) {
    case 'pending':
      return LL.billingPage.status.pending();
    case 'past_due':
      return LL.billingPage.status.pastDue();
    case 'paid':
      return LL.billingPage.status.paid();
    case 'void':
      return LL.billingPage.status.void();
    case 'other':
      return LL.billingPage.status.other();
  }
}

function mapPayment(
  payment: BillingPayment,
  locale: Locales,
  LL: TranslationFunctions
) {
  return {
    id: payment.id,
    date: formatDate(payment.date, locale),
    amount: formatCurrency(payment.amount, payment.currency, locale),
    status: payment.status,
    statusLabel: paymentStatusLabel(payment.status, LL),
    description: payment.description.trim() ? payment.description : '-',
    paymentUrl: payment.paymentUrl,
  };
}

export function mapBillingToViewModel(
  billing: BillingSnapshot,
  locale: Locales,
  LL: TranslationFunctions
): BillingViewModel {
  const hasSubscription = billing.plan !== null;

  return {
    hasSubscription,
    planName: billing.plan?.name ?? '',
    planPrice: billing.plan
      ? formatCurrency(billing.plan.priceAmount, billing.plan.currency, locale)
      : '',
    nextPaymentDate: billing.nextPaymentAt
      ? formatDate(billing.nextPaymentAt, locale)
      : '',
    hasStoredPaymentMethod: billing.hasStoredPaymentMethod,
    payments: billing.payments.map((payment) =>
      mapPayment(payment, locale, LL)
    ),
  };
}
